import React, { useState, useEffect } from "react";
import { View, Text, TouchableOpacity, ActivityIndicator, Platform } from "react-native";
import { useNavigation } from "@react-navigation/native";
import Ionicons from "@expo/vector-icons/Ionicons";
import { LinearGradient } from "expo-linear-gradient";
import {
    CreateResponsiveStyle,
    DEVICE_SIZES,
    useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppScreens } from "../../../Utility/AppScreens";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { ScreenBGGradientView } from "../../../../common/CommonView";
import { processPayment } from "../../../../services/profileAPI";
import { getLoginData } from "../../../../utility/AppStoreData";
import { toast } from "react-toastify";

type VerificationStatus = 'loading' | 'success' | 'error';

interface PaymentData {
    paymentStatus: string;
    issued: string;
    tokensAdded: number;
    totalTokens: number;
    redirectUrl: string;
}

const PaymentSuccess = ({ route }: any) => {
    const navigation = useNavigation();
    const styles = useStyles();
    const deviceSize = useDeviceSize();
    const { session_id, eventID } = route?.params || {};
    const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

    const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>('loading');
    const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
    const [countdown, setCountdown] = useState(8);  // 8 seconds countdown
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        verifyPayment();
    }, []);

    // Auto-redirect countdown timer
    useEffect(() => {
        if (verificationStatus === 'success' && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (verificationStatus === 'success' && countdown === 0 && paymentData?.redirectUrl) {
            // Redirect to original URL
            console.log('🔄 Redirecting to:', paymentData.redirectUrl);
            if (Platform.OS === 'web') {
                window.location.href = paymentData.redirectUrl;
            }
        }
    }, [verificationStatus, countdown, paymentData]);

    const verifyPayment = async () => {
        try {
            // Get session_id from URL query params first (Viva redirects use URL params)
            // Then fallback to route params for programmatic navigation
            let sessionId = null;

            if (Platform.OS === 'web') {
                const urlParams = new URLSearchParams(window.location.search);
                sessionId = urlParams.get('s') || urlParams.get('session_id');  // Viva uses 's' for order ID
            }

            // Fallback to route params if not in URL
            if (!sessionId) {
                sessionId = session_id;
            }

            console.log('🔍 Session ID from URL:', sessionId);
            console.log('🔍 URL params:', Platform.OS === 'web' ? window.location.search : 'N/A');

            if (!sessionId) {
                setErrorMessage('Payment session not found');
                setVerificationStatus('error');
                return;
            }

            // Get user's access token
            const userData = await getLoginData();
            const accessToken = userData?.AccessToken;

            if (!accessToken) {
                setErrorMessage('User not authenticated');
                setVerificationStatus('error');
                return;
            }

            // Call processPayment API
            const response = await processPayment(sessionId, accessToken);

            console.log('📦 API Response:', response);
            console.log('📦 Payment Status:', response.paymentStatus);

            // Parse response if it's a string
            let responseData = response;
            if (typeof response === 'string') {
                try {
                    responseData = JSON.parse(response);
                } catch (e) {
                    console.error('Failed to parse response:', e);
                    throw new Error('Invalid response format');
                }
            }

            // Validate response
            if (!responseData || typeof responseData !== 'object') {
                throw new Error('Invalid response data');
            }

            console.log('📦 Parsed Response:', responseData);
            console.log('📦 Payment Status:', responseData.paymentStatus);

            // Check if payment was successful by looking at paymentStatus field
            if (responseData.paymentStatus === 'completed') {
                console.log('✅ Payment verification successful:', responseData);
                console.log('📍 Redirect URL:', responseData.redirectUrl);

                setPaymentData(responseData);
                setVerificationStatus('success');
            } else {
                // Payment not completed or error response
                const errorMessage = responseData.error || responseData.message || 'Payment verification failed';

                console.error('❌ Payment verification failed:', responseData);
                console.error('❌ Payment Status:', responseData.paymentStatus);
                console.error('❌ Error Message:', errorMessage);

                setErrorMessage(errorMessage);
                setVerificationStatus('error');
            }
        } catch (error: any) {
            console.error('💥 Payment verification error:', error);
            console.error('💥 Error message:', error?.message);
            console.error('💥 Error stack:', error?.stack);

            setErrorMessage(error?.message || 'An error occurred during payment verification');
            setVerificationStatus('error');
            toast.error(error?.message || 'Payment verification failed');
        }
    };

    const handleGoHome = () => {
        if (Platform.OS === 'web') {
            window.location.href = '/dashboard';
        } else {
            navigateToScreen(navigation, AppScreens.HomeScreen);
        }
    };

    const handleViewTransactions = () => {
        if (Platform.OS === 'web') {
            window.location.href = '/profile/transactions';
        } else {
            navigateToScreen(navigation, AppScreens.ViewTrasactionHistory);
        }
    };

    const renderContent = () => {
        if (verificationStatus === 'loading') {
            return (
                <>
                    {/* Loading Icon */}
                    <View style={styles.iconContainer}>
                        <View style={styles.loadingIcon}>
                            <ActivityIndicator size={48} color={AppColors.white} />
                        </View>
                    </View>

                    {/* Message */}
                    <Text style={[AppFontStyle.BOLD_ANYBODY_18, { color: AppColors.white, textAlign: 'center', marginBottom: 8 }]}>
                        Verifying Payment
                    </Text>
                    <Text style={[AppFontStyle.REGULAR_ANYBODY_14, styles.messageText]}>
                        Please wait while we verify your payment status...
                    </Text>
                </>
            );
        }

        if (verificationStatus === 'error') {
            return (
                <>
                    {/* Error Icon */}
                    <View style={styles.iconContainer}>
                        <View style={styles.errorIcon}>
                            <Ionicons name="close" size={48} color={AppColors.white} />
                        </View>
                    </View>

                    {/* Message */}
                    <Text style={[AppFontStyle.BOLD_ANYBODY_18, { color: AppColors.white, textAlign: 'center', marginBottom: 8 }]}>
                        Verification Failed
                    </Text>
                    <Text style={[AppFontStyle.REGULAR_ANYBODY_14, styles.messageText]}>
                        {errorMessage || 'Unable to verify payment. Please contact support if amount was deducted.'}
                    </Text>
                </>
            );
        }

        // Success state
        return (
            <>
                {/* Success Icon */}
                <View style={styles.iconContainer}>
                    <View style={styles.successIcon}>
                        <Ionicons name="checkmark" size={48} color={AppColors.white} />
                    </View>
                </View>

                {/* Message */}
                <Text style={[AppFontStyle.REGULAR_ANYBODY_14, styles.messageText]}>
                    Your payment has been processed successfully.{"\n"}Thank you for your purchase!
                </Text>

                {/* Payment Details */}
                {paymentData && (
                    <View style={styles.detailsContainer}>
                        <View style={styles.detailRow}>
                            <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: "#9CA3AF" }]}>
                                Tokens Added
                            </Text>
                            <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
                                {paymentData.tokensAdded}
                            </Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: "#9CA3AF" }]}>
                                Amount Paid
                            </Text>
                            <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
                                {paymentData.issued}
                            </Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: "#9CA3AF" }]}>
                                Total Balance
                            </Text>
                            <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
                                {paymentData.totalTokens} tokens
                            </Text>
                        </View>
                    </View>
                )}

                {/* Transaction ID */}
                {session_id && (
                    <View style={styles.sessionContainer}>
                        <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: "#9CA3AF" }]}>
                            Transaction ID
                        </Text>
                        <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { color: AppColors.white, marginTop: 4 }]}>
                            {session_id.length > 24 ? `${session_id.substring(0, 24)}...` : session_id}
                        </Text>
                    </View>
                )}

                {/* Countdown */}
                {countdown > 0 && paymentData?.redirectUrl && (
                    <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: "#9CA3AF", textAlign: 'center', marginTop: 12 }]}>
                        Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
                    </Text>
                )}

                {/* Buttons */}
                <View style={[styles.buttonContainer, isMobile && { flexDirection: "column" }]}>
                    <TouchableOpacity style={styles.secondaryButton} onPress={handleViewTransactions}>
                        <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "#DC2626", textAlign: "center" }]}>
                            View Transactions
                        </Text>
                    </TouchableOpacity>

                    <TouchableOpacity style={styles.primaryButton} onPress={handleGoHome}>
                        <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white, textAlign: "center" }]}>
                            Go to Home
                        </Text>
                    </TouchableOpacity>
                </View>
            </>
        );
    };

    return (
        <View style={{ flex: 1 }}>
            {/* Platform background gradient */}
            <ScreenBGGradientView />

            {/* Content overlay */}
            <View style={[styles.container, isMobile && { justifyContent: "flex-end" }]}>
                <LinearGradient
                    colors={["#111827", "#000000"]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[
                        styles.mainView,
                        isMobile ? styles.mobileMainView : styles.desktopMainView,
                    ]}
                >
                    {/* Handle bar for mobile */}
                    {isMobile && <View style={styles.handleBar} />}

                    {/* Header */}
                    <View style={styles.headerRow}>
                        <Text style={[isMobile ? AppFontStyle.BOLD_ANYBODY_16 : AppFontStyle.BOLD_ANYBODY_18, { color: AppColors.white, flex: 1 }]}>
                            {verificationStatus === 'loading' ? 'Verifying Payment' :
                                verificationStatus === 'error' ? 'Verification Failed' :
                                    'Payment Successful'}
                        </Text>
                        <TouchableOpacity onPress={handleGoHome} style={styles.closeButton}>
                            <Ionicons name="close" size={isMobile ? 20 : 24} color={AppColors.white} />
                        </TouchableOpacity>
                    </View>

                    {/* Separator */}
                    <View style={styles.separator} />

                    {/* Dynamic Content */}
                    {renderContent()}
                </LinearGradient>
            </View>
        </View>
    );
};

export default PaymentSuccess;

const useStyles = CreateResponsiveStyle(
    {
        container: {
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
        },
        mainView: {
            alignSelf: "center",
            gap: 12,
            alignItems: "center",
        },
        desktopMainView: {
            width: 550,
            borderRadius: 8,
            borderWidth: 1.6,
            borderColor: "rgba(239, 68, 68, 0.50)",
            padding: 24,
        },
        mobileMainView: {
            width: "100%",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderWidth: 1,
            borderBottomWidth: 0,
            borderColor: "#DC2626",
            padding: 20,
            paddingHorizontal: 20,
            shadowColor: "rgba(232, 232, 232, 0.15)",
            shadowOffset: { width: 0, height: -3 },
            shadowOpacity: 1,
            shadowRadius: 14,
            elevation: 10,
        },
        handleBar: {
            width: 40,
            height: 4,
            backgroundColor: "#6B7280",
            borderRadius: 2,
            alignSelf: "center",
            marginBottom: 8,
        },
        headerRow: {
            width: "100%",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
        },
        closeButton: {
            padding: 4,
        },
        separator: {
            width: "100%",
            height: 1,
            backgroundColor: "#374151",
        },
        iconContainer: {
            alignItems: "center",
            marginVertical: 16,
        },
        successIcon: {
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: "#10B981",
            justifyContent: "center",
            alignItems: "center",
        },
        loadingIcon: {
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: "#3B82F6",
            justifyContent: "center",
            alignItems: "center",
        },
        errorIcon: {
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: "#EF4444",
            justifyContent: "center",
            alignItems: "center",
        },
        messageText: {
            color: "#9CA3AF",
            textAlign: "center",
            lineHeight: 22,
        },
        detailsContainer: {
            backgroundColor: "rgba(55, 65, 81, 0.3)",
            padding: 16,
            borderRadius: 8,
            width: "100%",
            gap: 12,
        },
        detailRow: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
        },
        sessionContainer: {
            backgroundColor: "rgba(55, 65, 81, 0.3)",
            padding: 16,
            borderRadius: 8,
            alignItems: "center",
            width: "100%",
        },
        buttonContainer: {
            flexDirection: "row",
            gap: 16,
            width: "100%",
            marginTop: 8,
        },
        secondaryButton: {
            flex: 1,
            backgroundColor: "transparent",
            borderWidth: 1,
            borderColor: AppColors.white,
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderRadius: 8,
            justifyContent: "center",
            alignItems: "center",
        },
        primaryButton: {
            flex: 1,
            backgroundColor: "#DC2626",
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderRadius: 8,
            justifyContent: "center",
            alignItems: "center",
        },
    },
    {
        [DEVICE_SIZES.SM]: {
            container: {
                justifyContent: "flex-end",
            },
        },
        [DEVICE_SIZES.XS]: {
            container: {
                justifyContent: "flex-end",
            },
        },
    }
);
